
import { Suspense } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const whatWeDo = [
  'Marka konumlama ve diferansiyasyon stratejisi',
  'Hedef kitle analizi ve persona geliştirme',
  'Kanal stratejisi ve içerik çerçevesi',
  'KPI belirleme ve ölçüm planı',
  'Rakip analizi ve pazar araştırması',
];

const forWhom = [
  'Yeni lansmanlar için konumlama arayan markalar',
  'Mevcut stratejisini güncellemek isteyen şirketler',
  'Dijital dönüşüm sürecindeki geleneksel markalar',
  'Premium segmente geçiş yapan işletmeler',
];

const deliverables = [
  'Marka strateji dokümanı',
  'Hedef kitle persona setleri',
  'Kanal ve içerik planı',
  'KPI dashboard ve ölçüm çerçevesi',
  'Rakip analiz raporu',
];

export default function Strategy() {
  const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [whatRef, whatInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [forRef, forInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [delivRef, delivInView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const scrollToContact = () => {
    window.REACT_APP_NAVIGATE('/');
    setTimeout(() => {
      const element = document.getElementById('contact');
      element?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  return (
    <div className="min-h-screen bg-cream">
      <Navbar />
      <main>
        <Suspense fallback={<div className="min-h-screen bg-cream"></div>}>
          {/* Hero */}
          <section ref={heroRef} className="pt-32 pb-24 bg-pastel-pink">
            <div className="max-w-4xl mx-auto px-6 text-center">
              <motion.span
                initial={{ opacity: 0, y: 20 }}
                animate={heroInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6 }}
                className="font-sans text-xs uppercase text-gray-medium tracking-wider"
              >
                STRATEGY
              </motion.span>
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={heroInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="font-serif text-5xl md:text-6xl text-charcoal mt-4 mb-6"
              >
                Konumlama ve Kanal Çerçevesi
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={heroInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="font-sans text-lg text-gray-medium leading-relaxed max-w-2xl mx-auto"
              >
                Markanızı doğru yerde, doğru kitleye, doğru mesajla konumlandırıyoruz. Veriye dayalı, ölçülebilir ve sürdürülebilir bir strateji çerçevesi oluşturuyoruz.
              </motion.p>
            </div>
          </section>

          {/* What We Do */}
          <section ref={whatRef} className="py-24 bg-white">
            <div className="max-w-4xl mx-auto px-6">
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                animate={whatInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6 }}
                className="font-serif text-4xl text-charcoal mb-12"
              >
                Ne Yapıyoruz
              </motion.h2>
              <div className="space-y-4">
                {whatWeDo.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={whatInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="flex items-start gap-4 p-4 bg-cream rounded-lg"
                  >
                    <i className="ri-check-line text-2xl text-charcoal flex-shrink-0 mt-1"></i>
                    <p className="font-sans text-base text-charcoal">{item}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* For Whom */}
          <section ref={forRef} className="py-24 bg-cream">
            <div className="max-w-4xl mx-auto px-6">
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                animate={forInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6 }}
                className="font-serif text-4xl text-charcoal mb-12"
              >
                Kimler İçin
              </motion.h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {forWhom.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={forInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="bg-white rounded-xl p-6 border border-gray-200"
                  >
                    <p className="font-sans text-base text-charcoal">{item}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* Deliverables */}
          <section ref={delivRef} className="py-24 bg-white">
            <div className="max-w-4xl mx-auto px-6">
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                animate={delivInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6 }}
                className="font-serif text-4xl text-charcoal mb-12"
              >
                Çıktılar
              </motion.h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {deliverables.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={delivInView ? { opacity: 1, scale: 1 } : {}}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="bg-pastel-pink rounded-lg p-5 font-sans text-sm text-charcoal font-medium"
                  >
                    {item}
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="py-24 bg-charcoal">
            <div className="max-w-4xl mx-auto px-6 text-center">
              <h2 className="font-serif text-4xl md:text-5xl text-white mb-8">
                Stratejinizi birlikte oluşturalım
              </h2>
              <button
                onClick={scrollToContact}
                className="bg-white text-charcoal px-8 py-4 rounded-full font-sans font-medium text-sm hover:bg-white/90 transition-all cursor-pointer whitespace-nowrap"
              >
                Brief Gönder
              </button>
            </div>
          </section>
        </Suspense>
      </main>
      <Footer />
    </div>
  );
}
